import {
  Button,
  ButtonGroup,
  Checkbox,
  DatePicker,
  DateRange,
  Modal,
  Select,
} from "@veneer/core";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import moment from "moment";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { AppointmentTypeEnum } from "../../../models/enum/AgendaApi/AppointmentTypeEnum";
import { MAX_AGENDA_RANGE_MONTHS } from "../agendaConstants";

const theme = createTheme({
  palette: {
    primary: {
      main: "#027aae",
    },
  },
});

export const AgendaEditingBLock = (props: any) => {
  const {
    showModalEditAgendaPermanent,
    cancelEditAgendaPermanent,
    editAgendaButtonDisable,
    submitEditAgenda,
    handleChangeTypeOfVisit,
    typeofAppointmentData,
    typeOfVisit,
    onClearTypeOfVisit,
    handleChangeSubRetail,
    subretailData,
    subRetail,
    onClearSubRetail,
    setPointOfSale,
    pointOfSaleData,
    pointOfSale,
    errorPos,
    endDate,
    handleChangeStartDate,
    startDate,
    errorDatePicker,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    checkboxMonday,
    setCheckboxMonday,
    checkboxTuesday,
    setCheckboxTuesday,
    checkboxWednesday,
    setCheckboxWednesday,
    checkboxThursday,
    setCheckboxThursday,
    checkboxFriday,
    setcheckboxFriday,
    checkboxSaturday,
    setCheckboxSaturday,
    checkboxSunday,
    setCheckboxSunday,
    showModalEditAgendaVisit,
    cancelEditAgendaVisit,
    handleChangeVisitDate,
    visitDate,
    editOccurrenceRef,
    startTimeErrorReason,
    setStartTimeErrorReason,
    endTimeErrorReason,
    setEndTimeErrorReason,
  } = props;

  const [t] = useTranslation("global");
  const appointmentTypeId = Number(typeOfVisit?.[0]);
  const isPermanentType = appointmentTypeId === AppointmentTypeEnum.Permanent;
  const isEditableType =
    appointmentTypeId === AppointmentTypeEnum.Permanent ||
    appointmentTypeId === AppointmentTypeEnum.Route;

  const timesAreEqual =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isSame(moment(startTime), 'minute');

  // Direct comparison so validation stays correct when startTime changes after endTime is set
  const endTimeIsBeforeStart =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isBefore(moment(startTime), 'minute');

  const endTimeInvalid = timesAreEqual || endTimeIsBeforeStart;

  let endTimeErrorMessage = t("header.endTimeInvalidError");
  if (timesAreEqual) {
    endTimeErrorMessage = t("header.startEndTimeSameError");
  } else if (endTimeErrorReason === 'minTime' || endTimeIsBeforeStart) {
    endTimeErrorMessage = t("header.endTimeBeforeStartError");
  }

  const timeErrorBlock = (
    <div className="agendaTimesErrorBlock">
      {startTimeErrorReason && (
        <p className="agendaTimePickerError">
          {startTimeErrorReason === 'minTime'
            ? t("header.startTimePastError")
            : t("header.startTimeInvalidError")}
        </p>
      )}
      {(endTimeErrorReason || endTimeInvalid) && (
        <p className="agendaTimePickerError">{endTimeErrorMessage}</p>
      )}
    </div>
  );

  return (
    <>
      {showModalEditAgendaPermanent && !showModalEditAgendaVisit && (
        <Modal
          closeButton={true}
          align="center"
          id="modalEditAgenda"
          show={showModalEditAgendaPermanent}
          onClose={cancelEditAgendaPermanent}
          footer={
            <ButtonGroup className="createAgendaButtongrouop">
              <Button
                disabled={editAgendaButtonDisable}
                onClick={submitEditAgenda}
              >
                {t("header.submitEdit")}
              </Button>
              <Button
                onClick={cancelEditAgendaPermanent}
                appearance="secondary"
              >
                {t("header.cancel")}
              </Button>
            </ButtonGroup>
          }
          title={t("header.editAgenda")}
        >
          <div className="createAgenda">
            <Select
              className="createAgendaSelect"
              label={t("header.typeOfVisit")}
              onChange={(e) => handleChangeTypeOfVisit(e.value)}
              options={typeofAppointmentData}
              value={typeOfVisit}
              onClear={() => onClearTypeOfVisit()}
              disabled={true}
              readOnly
            />

            <div className="createAgendaPermanent">
              <Select
                className="createAgendaSelect"
                label={t("header.subRetail")}
                onChange={(e) => handleChangeSubRetail(e.value)}
                options={subretailData}
                value={subRetail}
                onClear={() => onClearSubRetail()}
                disabled={!isEditableType}
                readOnly={!isEditableType}
              />
              <Select
                className="createAgendaSelect"
                label={t("header.pointOfSale")}
                onChange={(e) => setPointOfSale([e.value])}
                options={pointOfSaleData}
                value={pointOfSale}
                onClear={() => setPointOfSale([])}
                error={errorPos}
              />

              <DatePicker
                className="createAgendaSelect createAgendaDatePicker"
                isOutsideRange={(day) =>
                  day.isBefore(moment(), "day") ||
                  day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day")
                }
                clearIcon={false}
                endDate={endDate}
                onChange={(date) => handleChangeStartDate(date as DateRange)}
                orientation="horizontal"
                range
                startDatePlaceholder="mm/dd/yyyy"
                endDatePlaceholder="mm/dd/yyyy"
                format="LL"
                startDate={startDate}
                startDateLabel={t("header.stardDate")}
                endDateLabel={t("header.endDate")}
                error={errorDatePicker}
              />

              {timeErrorBlock}

              <div className="createAgendaTimes">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <ThemeProvider theme={theme}>
                    <div className="agendaTimePickerCell">
                      <TimePicker
                        className="createAgendaTimePicker"
                        label={t("header.startTime")}
                        value={startTime}
                        onChange={(value) => setStartTime(value)}
                        onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                        renderInput={(params) => <TextField {...params} />}
                        minutesStep={5}
                        ampm={false}
                        minTime={
                          // Only restrict to current time when the occurrence being
                          // edited (occurrenceDate from GET response) is today.
                          // startDate is the series date range — never used here.
                          editOccurrenceRef &&
                          moment(editOccurrenceRef).isSame(moment(), 'day')
                            ? moment()
                            : undefined
                        }
                      />
                    </div>

                    <div className="agendaTimePickerCell">
                      <TimePicker
                        className="createAgendaTimePicker"
                        label={t("header.endTime")}
                        value={endTime}
                        minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                        onChange={(value) => setEndTime(value)}
                        onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                        renderInput={(params) => (
                          <TextField {...params} error={params.error || endTimeInvalid} />
                        )}
                        minutesStep={5}
                        ampm={false}
                      />
                    </div>
                  </ThemeProvider>
                </LocalizationProvider>
              </div>

              <div className="createAgendaDays">
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.monday")}
                  value={checkboxMonday}
                  checked={checkboxMonday}
                  onChange={() => setCheckboxMonday(!checkboxMonday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.tuesday")}
                  value={checkboxTuesday}
                  checked={checkboxTuesday}
                  onChange={() => setCheckboxTuesday(!checkboxTuesday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.wednesday")}
                  value={checkboxWednesday}
                  checked={checkboxWednesday}
                  onChange={() => setCheckboxWednesday(!checkboxWednesday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.thursday")}
                  value={checkboxThursday}
                  checked={checkboxThursday}
                  onChange={() => setCheckboxThursday(!checkboxThursday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.friday")}
                  value={checkboxFriday}
                  checked={checkboxFriday}
                  onChange={() => setcheckboxFriday(!checkboxFriday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.saturday")}
                  value={checkboxSaturday}
                  checked={checkboxSaturday}
                  onChange={() => setCheckboxSaturday(!checkboxSaturday)}
                />
                <Checkbox
                  className="createAgendaCheckbox"
                  label={t("header.sunday")}
                  value={checkboxSunday}
                  checked={checkboxSunday}
                  onChange={() => setCheckboxSunday(!checkboxSunday)}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}

      {!showModalEditAgendaPermanent && showModalEditAgendaVisit && (
        <Modal
          closeButton={true}
          align="center"
          id="modalEditAgenda"
          show={showModalEditAgendaVisit}
          onClose={cancelEditAgendaVisit}
          footer={
            <ButtonGroup className="createAgendaButtongrouop">
              <Button
                disabled={editAgendaButtonDisable}
                onClick={submitEditAgenda}
              >
                {t("header.submitEdit")}
              </Button>
              <Button onClick={cancelEditAgendaVisit} appearance="secondary">
                {t("header.cancel")}
              </Button>
            </ButtonGroup>
          }
          title={t("header.editAgenda")}
        >
          <div className="createAgenda">
            <Select
              className="createAgendaSelect"
              label={t("header.typeOfVisit")}
              onChange={(e) => handleChangeTypeOfVisit(e.value)}
              options={typeofAppointmentData}
              value={typeOfVisit}
              onClear={() => onClearTypeOfVisit()}
              disabled={true}
              readOnly
            />
            <div className="createAgendaVisit">
              <Select
                className="createAgendaSelect"
                label={t("header.pointOfSale")}
                onChange={(e) => setPointOfSale([e.value])}
                options={pointOfSaleData}
                value={pointOfSale}
                onClear={() => setPointOfSale([])}
                error={errorPos}
              />

              <DatePicker
                className="createAgendaSelect createAgendaDatePicker"
                label={t("header.visitDate")}
                onChange={handleChangeVisitDate}
                clearIcon={false}
                value={visitDate}
                placeholder="mm/dd/yyyy"
                format="L"
                isOutsideRange={(day) =>
                  day.isBefore(moment(), "day") ||
                  day.isAfter(moment().add(MAX_AGENDA_RANGE_MONTHS, "months"), "day")
                }
              />
              {timeErrorBlock}

              <div className="createAgendaTimes">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <ThemeProvider theme={theme}>
                    <TimePicker
                      className="createAgendaTimePicker"
                      label={t("header.startTime")}
                      value={startTime}
                      onChange={(value) => setStartTime(value)}
                      onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                      renderInput={(params) => <TextField {...params} />}
                      minutesStep={5}
                      ampm={false}
                      minTime={visitDate && moment(visitDate).isSame(moment(), 'day') ? moment() : undefined}
                    />

                    <TimePicker
                      className="createAgendaTimePicker"
                      label={t("header.endTime")}
                      minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                      value={endTime}
                      onChange={(value) => setEndTime(value)}
                      onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                      renderInput={(params) => (
                        <TextField {...params} error={params.error || endTimeInvalid} />
                      )}
                      minutesStep={5}
                      ampm={false}
                    />
                  </ThemeProvider>
                </LocalizationProvider>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
